import { createSelector } from '@reduxjs/toolkit';
import { last, memoize, orderBy } from 'lodash';

import { InsightsState, InsightsStateSelector } from 'app-redux/InsightsState';
import * as dto from 'client/dto';
import { RiskLikelihood, RiskMagnitude } from 'models/RiskCategories';
import { parseIsoDateStartOfDayUtc0 } from 'utils/dateTimeHelpers';
import { calculateToWholePercent } from 'utils/mathHelpers';
import { getRiskLikelihood, getRiskMagnitude } from 'utils/modelHelpers';
import { oxford } from 'utils/stringFormattingHelpers';

import { SparklineDatumType } from 'components/_graphs/_common/DiseaseActivityModels';

import { RiskOverviewFilterItem } from './RiskOverviewFilterModel';

const isLoading: InsightsStateSelector<boolean> = state => state?.riskOverview.isLoading;
const getRiskFeedEntries = (state: InsightsState) => state?.riskOverview.entriesByActivityIdAndLocationId;
const sortedRiskFeedPositions: InsightsStateSelector<dto.RiskFeedPosition[]> = state => state?.riskOverview.sortedRiskFeedPositions;
const lastRiskFeedPosition: InsightsStateSelector<dto.RiskFeedPosition> = createSelector([sortedRiskFeedPositions], sortedRiskFeedPositions =>
  last(sortedRiskFeedPositions)
);

const getRiskFeedEntryByActivityIdAndLocationId = createSelector(getRiskFeedEntries, entries =>
  memoize(
    (activityId: number, locationId: number) => entries?.[activityId]?.[locationId],
    (activityId, locationId) => `ro-riskfeed-entry-${activityId}-${locationId}`
  )
);

const shouldDisplayLoadMore: InsightsStateSelector<boolean> = state => state.riskOverview.shouldDisplayLoadMore;
const getFilteredLocationIds: InsightsStateSelector<number[]> = createSelector(
  state => state.riskOverview.locationNamesById,
  state => state.riskOverview.filteredItems,
  (locationNamesById, filteredItems) =>
    orderBy(
      filteredItems.filter(x => x.type === 'location' && Object.keys(locationNamesById).includes(`${x.id}`)).map(x => x.id),
      x => locationNamesById[x]
    )
);
const getFilteredDiseaseIds: InsightsStateSelector<number[]> = createSelector(
  state => state.riskOverview.diseaseNamesById,
  state => state.riskOverview.filteredItems,
  (diseaseNamesById, filteredItems) =>
    orderBy(
      filteredItems.filter(x => x.type === 'disease' && Object.keys(diseaseNamesById).includes(`${x.id}`)).map(x => x.id),
      x => diseaseNamesById[x]
    )
);
const getFilteredItems: InsightsStateSelector<RiskOverviewFilterItem[]> = state => state?.riskOverview?.filteredItems;
const isFilterResetButtonEnabled: InsightsStateSelector<boolean> = state => !!state?.riskOverview?.filteredItems?.length;
const getNumOfRiskFactors: (diseaseActivityId: number, locationId: number) => InsightsStateSelector<number> =
  (diseaseActivityId, locationId) => state =>
    state.riskOverview.entriesByActivityIdAndLocationId[diseaseActivityId]?.[locationId]?.riskFactorNumber ?? 0;
const getIsTreatmentAvailable: (diseaseActivityId: number, locationId: number) => InsightsStateSelector<boolean> =
  (diseaseActivityId, locationId) => state =>
    state.riskOverview.entriesByActivityIdAndLocationId[diseaseActivityId]?.[locationId]?.isTreatmentAvailable;
const getReadableAcquisitionSources: (diseaseActivityId: number, locationId: number) => InsightsStateSelector<string> =
  (diseaseActivityId, locationId) => state => {
    const riskFeedEntry = state.riskOverview.entriesByActivityIdAndLocationId[diseaseActivityId]?.[locationId];
    if (riskFeedEntry?.acquisitionSources) {
      const knownAcquisitionSources = riskFeedEntry.acquisitionSources.filter(x => x.toLocaleLowerCase() !== 'unknown');
      return oxford(knownAcquisitionSources, 'and');
    }

    return '';
  };
const getPreventabilityByDiseaseActivityIdAndLocationId: (
  diseaseActivityId: number,
  locationId: number
) => InsightsStateSelector<dto.PreventabilityDisruptionFactorType> = (diseaseActivityId, locationId) => state =>
  state.riskOverview.entriesByActivityIdAndLocationId[diseaseActivityId]?.[locationId]?.disruption?.preventability;
const getSeverityByDiseaseActivityIdAndLocationId: (
  diseaseActivityId: number,
  locationId: number
) => InsightsStateSelector<dto.SeverityDisruptionFactorType> = (diseaseActivityId, locationId) => state =>
  state.riskOverview.entriesByActivityIdAndLocationId[diseaseActivityId]?.[locationId]?.disruption?.severity ??
  dto.SeverityDisruptionFactorType.NoData;
const getDiseaseIdByActivityIdAndLocationId: (diseaseActivityId: number, locationId: number) => InsightsStateSelector<number> =
  (diseaseActivityId, locationId) => state =>
    state.riskOverview.entriesByActivityIdAndLocationId[diseaseActivityId]?.[locationId]?.diseaseId;

const getImportationRiskMagnitude: (diseaseActivityId: number, locationId: number) => InsightsStateSelector<RiskMagnitude> =
  (diseaseActivityId, locationId) => state =>
    state.riskOverview.entriesByActivityIdAndLocationId[diseaseActivityId]
      ? getRiskMagnitude(state.riskOverview.entriesByActivityIdAndLocationId[diseaseActivityId]?.[locationId]?.importationRisk)
      : null;
const getImportationRiskLikelihood: (diseaseActivityId: number, locationId: number) => InsightsStateSelector<RiskLikelihood> =
  (diseaseActivityId, locationId) => state =>
    state.riskOverview.entriesByActivityIdAndLocationId[diseaseActivityId]
      ? getRiskLikelihood(state.riskOverview.entriesByActivityIdAndLocationId[diseaseActivityId]?.[locationId]?.importationRisk)
      : 'Not calculated';
const getImportationSourcesByActivityIdAndLocationId: (
  diseaseActivityId: number,
  locationId: number
) => InsightsStateSelector<dto.GetGeonameModel[]> = (diseaseActivityId, locationId) => state =>
  state.riskOverview.importationSourcesByActivityIdAndLocationId[diseaseActivityId]?.[locationId];

const isLoadingImportationSources = (diseaseActivityId: number, locationId: number) => (state: InsightsState) =>
  state.riskOverview.isLoadingImportationSources[diseaseActivityId]?.[locationId];

const getRiskFeedSortType: InsightsStateSelector<dto.RiskFeedSortType> = state => state?.riskOverview?.riskFeedSortType;

const getAoiVsCountryCasesGraphData: (locationId: number, diseaseId: number) => InsightsStateSelector<SparklineDatumType[]> =
  (locationId, diseaseId) => state => {
    return (state?.riskOverview.proximalCasesByLocationIdAndDiseaseId?.[diseaseId]?.[locationId] || []).map(
      x =>
        ({
          date: parseIsoDateStartOfDayUtc0(x.startDate),
          cases: x.countryCaseCounts?.reportedCases || 0,
          nearby: x.caseCounts?.reportedCases || 0,
        } as SparklineDatumType)
    );
  };
const getRiskFeedEntryDisruptionFactors: (diseaseActivityId: number, locationId: number) => InsightsStateSelector<dto.DisruptionModel> =
  (diseaseActivityId, locationId) => state => {
    const disruptionModel = state.riskOverview.entriesByActivityIdAndLocationId[diseaseActivityId]?.[locationId]?.disruption;

    if (!disruptionModel) return null;

    return { ...disruptionModel, locationResiliency: calculateToWholePercent(disruptionModel?.locationResiliency) };
  };

const getRiskFeedEntryMortalityRates: (diseaseActivityId: number, locationId: number) => InsightsStateSelector<dto.DiseaseMortalityRatesModel> =
  (diseaseActivityId, locationId) => state =>
    state.riskOverview?.entriesByActivityIdAndLocationId[diseaseActivityId]?.[locationId]?.mortalityRates ?? null;

const getPreviousFrontPageStateId: InsightsStateSelector<string> = state => state.riskOverview.previousFrontPageStateId ?? '';

const getNestedCasesFlat: (
  diseaseId: number,
  locationId: number
) => InsightsStateSelector<{ locationName: string; cases: number; isGlobal: boolean }[]> = (diseaseId, locationId) => state => {
  const nestedCases = state?.riskOverview?.nestedCaseCountsByDiseaseIdAndLocationId?.[diseaseId]?.[locationId];

  if (!nestedCases) return [];

  const res: { locationName: string; cases: number; isGlobal: boolean }[] = [];
  res.push({
    locationName: nestedCases.locationCaseCounts.locationName,
    cases: nestedCases.locationCaseCounts.caseCounts?.reportedCases,
    isGlobal: false,
  });

  if (nestedCases.countyCaseCounts) {
    res.push({
      locationName: nestedCases.countyCaseCounts.locationName,
      cases: nestedCases.countyCaseCounts.caseCounts?.reportedCases,
      isGlobal: false,
    });
  }

  if (nestedCases.metroAreaCaseCounts) {
    res.push({
      locationName: nestedCases.metroAreaCaseCounts.locationName,
      cases: nestedCases.metroAreaCaseCounts.caseCounts?.reportedCases,
      isGlobal: false,
    });
  }

  if (nestedCases.provinceCaseCounts) {
    res.push({
      locationName: nestedCases.provinceCaseCounts.locationName,
      cases: nestedCases.provinceCaseCounts.caseCounts?.reportedCases,
      isGlobal: false,
    });
  }

  if (nestedCases.countryCaseCounts) {
    res.push({
      locationName: nestedCases.countryCaseCounts.locationName,
      cases: nestedCases.countryCaseCounts.caseCounts?.reportedCases,
      isGlobal: false,
    });
  }

  res.push({
    locationName: 'Global',
    cases: nestedCases.globalCaseCounts?.reportedCases,
    isGlobal: true,
  });

  return res;
};

const getLocationFromNestedCasesTree: (diseaseId: number, locationId: number) => InsightsStateSelector<dto.CaseReportModel> =
  (diseaseId, locationId) => state =>
    state?.riskOverview?.nestedCaseCountsByDiseaseIdAndLocationId?.[diseaseId]?.[locationId]?.locationCaseCounts;

const getCountryFromNestedCasesTree: (diseaseId: number, locationId: number) => InsightsStateSelector<dto.CaseReportModel> =
  (diseaseId, locationId) => state =>
    state?.riskOverview?.nestedCaseCountsByDiseaseIdAndLocationId?.[diseaseId]?.[locationId]?.countryCaseCounts ??
    state?.riskOverview?.nestedCaseCountsByDiseaseIdAndLocationId?.[diseaseId]?.[locationId]?.locationCaseCounts;

const hasIncidenceCaseData = (diseaseId: number, locationId: number) => (state: InsightsState) =>
  state?.riskOverview?.hasIncidenceCaseCounts?.[diseaseId]?.[locationId];

const getLastUpdatedByDiseaseIdAndLocationId: (diseaseId: number, locationId: number) => InsightsStateSelector<string> =
  (diseaseId, locationId) => state =>
    state?.riskOverview?.lastCheckByDiseaseIdAndLocationId?.[diseaseId]?.[locationId] ?? null;

const isLoadingRiskIncidenceByDiseaseIdAndLocationId: (diseaseId: number, locationId: number) => InsightsStateSelector<boolean> =
  (diseaseId, locationId) => state =>
    state?.riskOverview?.isLoadingRiskIncidenceByDiseaseIdAndLocationId?.[diseaseId]?.[locationId] ?? false;

const getCardListErrorState = (state: InsightsState) =>
  state.riskOverview.hasError ? 'error' : !state.riskOverview.sortedRiskFeedPositions?.length ? 'no-cards' : false;

const downloadingPngs = (state: InsightsState) => state?.riskOverview?.downloadingPngs;

// riskOverviewSelectors
export default {
  isLoading,
  sortedRiskFeedPositions,
  shouldDisplayLoadMore,
  lastRiskFeedPosition,
  getRiskFeedEntryByActivityIdAndLocationId,
  getFilteredLocationIds,
  getFilteredDiseaseIds,
  getFilteredItems,
  isFilterResetButtonEnabled,
  getNumOfRiskFactors,
  getIsTreatmentAvailable,
  getReadableAcquisitionSources,
  getPreventabilityByDiseaseActivityIdAndLocationId,
  getSeverityByDiseaseActivityIdAndLocationId,
  getDiseaseIdByActivityIdAndLocationId,
  getImportationRiskMagnitude,
  getImportationRiskLikelihood,
  getImportationSourcesByActivityIdAndLocationId,
  isLoadingImportationSources,
  getRiskFeedSortType,
  getAoiVsCountryCasesGraphData,
  getRiskFeedEntryDisruptionFactors,
  getRiskFeedEntryMortalityRates,
  getPreviousFrontPageStateId,
  getNestedCasesFlat,
  getCountryFromNestedCasesTree,
  getLastUpdatedByDiseaseIdAndLocationId,
  getLocationFromNestedCasesTree,
  hasIncidenceCaseData,
  isLoadingRiskIncidenceByDiseaseIdAndLocationId,
  getCardListErrorState,
  downloadingPngs,
};
